'use client';

// Bridges FCM push messages → call store
// When an FCM data message arrives with type 'call:incoming',
// the adapter writes it into the Zustand call store so IncomingCallHost renders.
// De-duplication by callId prevents races when Socket.IO also delivers the event.

import { useStaffCallStore } from '../hooks/useStaffCallClient';
import { PUSH_TYPE_INCOMING_CALL } from '@/shared/constants/push-message-types';

export interface CallPushPayload {
  type: string;
  callId: string;
  bookingId?: string;
  /**
   * The caller's name.
   *
   * `callerName` is what the SERVER sends (`call-notifications.ts`);
   * `guestName` is what the service worker forwards from older payloads. Both
   * are accepted because reading only `guestName` — which is what this did —
   * meant every push-originated call rendered as "Guest" with the real name
   * sitting unread in the payload.
   */
  callerName?: string;
  guestName?: string;
  hotelName?: string;
  propertyId?: string;
  deeplink?: string;
}

export function handlePushMessage(payload: CallPushPayload): boolean {
  if (payload.type !== PUSH_TYPE_INCOMING_CALL) return false;

  // A push with no call id cannot be acted on — accepting it would put the
  // ringing panel on screen with an id the gateway will reject.
  if (!payload.callId) return false;

  const store = useStaffCallStore.getState();

  if (store.state !== 'idle') return false;

  if (
    store.incomingCall &&
    store.incomingCall.callId === payload.callId
  ) {
    return false;
  }

  store.setIncoming({
    callId: payload.callId,
    guestName: payload.callerName ?? payload.guestName ?? 'Guest',
    hotelName: payload.hotelName ?? 'Hotel',
    bookingId: payload.bookingId ?? '',
    origin: 'push',
  });

  return true;
}

export function handleServiceWorkerMessage(
  event: MessageEvent
): void {
  const data = event.data as CallPushPayload | undefined;
  if (!data?.type) return;
  handlePushMessage(data);
}
